import type { ComponentType, ReactNode } from 'react';
import styles from './activitatspopulars.module.scss';
import Enllac from '../../Enllac/Enllac';
import Icona from '../../Icona/Icona';
import __isEmpty from 'lodash/isEmpty';
import { ActivitatsPopularsProps, Acte, Activitat, Entitat, Poblacio } from './ActivitatsPopulars.types';

const EnllacExternal = Enllac as ComponentType<{
    href?: string;
    title?: string;
    className?: string;
    novaFinestra?: boolean;
    children: ReactNode;
}>;

function toArray<T>(val: T | T[] | undefined | null): T[] {
    if (val === undefined || val === null) return [];
    return Array.isArray(val) ? val : [val];
}

function buildSocialUrl(platform: 'facebook' | 'twitter' | 'instagram', value: string): string {
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    switch (platform) {
        case 'facebook':
            return `https://www.facebook.com/${value}`;
        case 'twitter':
            return `https://x.com/${value}`;
        case 'instagram':
            return `https://www.instagram.com/${value}/`;
    }
}

function buildWebUrl(value: string): string | null {
    if (value.startsWith('https://') || value.startsWith('http://')) return value;
    return null;
}

function formatAmount(value: string): string {
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return Math.round(num).toLocaleString('ca') + '€';
}

export default function ActivitatsPopulars(props: Readonly<ActivitatsPopularsProps>) {
    const poblacioRaw = props?.items;

    if (__isEmpty(poblacioRaw)) return null;

    const poblacions: Poblacio[] = toArray(poblacioRaw as Poblacio | Poblacio[]);

    return (
        <div className={styles.container}>
            {poblacions.map((poblacio) => {
                const entitats: Entitat[] = toArray(poblacio.CCMAentitat);

                return (
                    <section key={poblacio.ACTPOB} className={styles.seccio}>
                        <h2 className={styles.titolPoblacio}>{poblacio.ACTPOB}</h2>
                        {entitats.map((entitat) => {
                            const activitats: Activitat[] = toArray(entitat.activitat);
                            const facebookUrl = entitat.facebook ? buildSocialUrl('facebook', entitat.facebook) : null;
                            const twitterUrl = entitat.twitter ? buildSocialUrl('twitter', entitat.twitter) : null;
                            const instagramUrl = entitat.instagram ? buildSocialUrl('instagram', entitat.instagram) : null;
                            const webUrl = entitat.web ? buildWebUrl(entitat.web) : null;
                            return (
                                <div key={entitat.IDENTI} className={styles.entitat}>
                                    <h3 className={styles.nomEntitat}>{entitat.ENTITAT}</h3>

                                    {activitats.map((activitat) => {
                                        const actes: Acte[] = toArray(activitat.actes?.acte);
                                        return (
                                            <div key={activitat.IDACTIV} className={styles.activitat}>
                                                {!__isEmpty(activitat.ACTTIT) && (
                                                    <h4 className={styles.titolActivitat}>
                                                        {(activitat.ACTTIT as string).toUpperCase()}
                                                    </h4>
                                                )}
                                                {actes.map((acte) => (
                                                    <div key={acte.IDACCIO} className={styles.acte}>
                                                        {acte.ACTDATA && (
                                                            <p className={styles.infoActe}>
                                                                <span className={styles.acteLabel}>Data:</span> {acte.ACTDATA}
                                                            </p>
                                                        )}
                                                        {acte.ACTHORA && (
                                                            <p className={styles.infoActe}>
                                                                <span className={styles.acteLabel}>Hora:</span> {acte.ACTHORA}
                                                            </p>
                                                        )}
                                                        {acte.ACTLLOC && (
                                                            <p className={styles.infoActe}>
                                                                <span className={styles.acteLabel}>Lloc:</span> {acte.ACTLLOC}
                                                            </p>
                                                        )}
                                                        {acte.ACTDESCR && (
                                                            <p className={styles.descActe}>
                                                                <strong>{"L'entitat informa: "}</strong>
                                                                {acte.ACTDESCR}
                                                            </p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })}

                                    {entitat.RECAPTACIO && (
                                        <p className={styles.recaptacio}>
                                            <strong>Portem recaptats: </strong>
                                            {formatAmount(entitat.RECAPTACIO)}
                                        </p>
                                    )}

                                    {(facebookUrl || twitterUrl || instagramUrl || webUrl) && (
                                        <ul className={styles.xarxes}>
                                            {facebookUrl && (
                                                <li>
                                                    <EnllacExternal
                                                        href={facebookUrl}
                                                        title='Facebook'
                                                        novaFinestra={true}
                                                        className={styles.xarxaEnllac}
                                                    >
                                                        <Icona icona='facebook' alt='Facebook' mida={24} />
                                                    </EnllacExternal>
                                                </li>
                                            )}
                                            {twitterUrl && (
                                                <li>
                                                    <EnllacExternal
                                                        href={twitterUrl}
                                                        title='X'
                                                        novaFinestra={true}
                                                        className={styles.xarxaEnllac}
                                                    >
                                                        <Icona icona='x' alt='X' mida={24} />
                                                    </EnllacExternal>
                                                </li>
                                            )}
                                            {instagramUrl && (
                                                <li>
                                                    <EnllacExternal
                                                        href={instagramUrl}
                                                        title='Instagram'
                                                        novaFinestra={true}
                                                        className={styles.xarxaEnllac}
                                                    >
                                                        <Icona icona='instagram' alt='Instagram' mida={24} />
                                                    </EnllacExternal>
                                                </li>
                                            )}
                                            {webUrl && (
                                                <li>
                                                    <EnllacExternal
                                                        href={webUrl}
                                                        title='Web'
                                                        novaFinestra={true}
                                                        className={styles.xarxaEnllac}
                                                    >
                                                        <span className={styles.textWeb}>Web </span>
                                                        <Icona
                                                            icona='linkexterior'
                                                            className={styles.iconaNovaFinestra}
                                                            alt='Web'
                                                            mida={24}
                                                        />
                                                    </EnllacExternal>
                                                </li>
                                            )}
                                        </ul>
                                    )}
                                </div>
                            );
                        })}
                    </section>
                );
            })}
        </div>
    );
}
